import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';

import Table from '~components/molecules/Table';
import Pagination from '~components/atoms/Pagination';
import Text from '~components/atoms/Text';

import UtilFunctions from '~utils/functions';

import * as Styles from './_customerManagementCustomerCollateralTable.module.scss';
import ActionButton from '~components/atoms/ActionButton';
import { TDeleteCustomerCollateralReduxParams } from '~types/feat/customer';
import { TransactionStatusEnum } from '~utils/enums';
import dayjs from 'dayjs';

const CustomerManagementCustomerCollateralTable = () => {
    const {
        Misc:
        {
            dateTimeFormat,
            renderTransactionStatusChip,
        },
    } = UtilFunctions;

    const dispatch = useAppDispatch();

    const isLoading = useAppSelector(state => state.customer.loadings.getCustomerCollateral);
    const { index, maxIndex, data: customerCollaterals } = useAppSelector(state => state.customer.customerCollateral);
    const customerId = useAppSelector(state => state.customer.customerDetails.customerId);

    useEffect(() => {
        dispatch(combinedActions.customerGetCustomerCollateralListingAttempt({ customerId }));
    }, [customerId]);

    const setIndex = (idx: number) => dispatch(combinedActions.customerSetCustomerCollateralIndex({ index: idx }));
    const setIsOpen = (open: boolean) => dispatch(combinedActions.customerSetCustomerCollateralModalIsOpen(open));
    const setDeleteCustomerCollateralState = (params: TDeleteCustomerCollateralReduxParams) => dispatch(combinedActions.customerSetCustomerCollateralState(params));

    // if maxIndex is lower then index reset index to 0
    useEffect(() => {
        if (index > maxIndex) {
            setIndex(0);
        }
    }, [maxIndex]);

    const handleOnPrev = () => {
        if (index > 0) setIndex(index - 1);
    };
    const handleOnNext = () => {
        if (index < maxIndex) setIndex(index + 1);
    };
    const handleOnFirst = () => setIndex(0);
    const handleOnLast = () => setIndex(maxIndex >= 0 ? maxIndex : 0);

    const handleViewCollateralDetails = (collateralId: number) => {
        dispatch(combinedActions.customerSetCustomerCollateralState({ customerId, collateralId }));
        dispatch(combinedActions.customerSetCollateralDetailsModalIsOpen(true));
    };

    const handleCancelCollateralClicked = (collateralId: number) => {
        setDeleteCustomerCollateralState({ customerId, collateralId });
        setIsOpen(true);
    };

    const tableHeader = [
        'COLLATERAL AMOUNT',
        'PRODUCT NAME',
        'STATUS',
        'EXPIRY DATE',
        'ACTION',
    ];

    const tableBody = customerCollaterals.map(collateral => {
        const {
            amount,
            marketValue,
            productName,
            status,
            collateralId,
            collateralEndDate,
        } = collateral;

        const massagedData = [
            `RM ${(Math.round(Number(marketValue) * 100) / 100).toFixed(2)} (${amount}g)`,
            productName,
            renderTransactionStatusChip(status, TransactionStatusEnum[status]),
            dateTimeFormat(collateralEndDate),
            (
                status === TransactionStatusEnum.Approved
                && dayjs().toISOString() <= dayjs(collateralEndDate).toISOString() ?
                    (
                        <div
                            key={collateralId}
                            className={Styles['button-container']}
                        >
                            <div>
                                <ActionButton
                                    label='View Details'
                                    onClick={() => handleViewCollateralDetails(collateralId)}
                                />

                                <ActionButton
                                    label='Cancel Collateral'
                                    onClick={() => handleCancelCollateralClicked(collateralId)}
                                    className={Styles.danger}
                                />
                            </div>
                        </div>
                    )
                    :
                    (
                        <div
                            key={collateralId}
                            className={Styles['button-container']}
                        >
                            <div>
                                <ActionButton
                                    label='View Details'
                                    onClick={() => handleViewCollateralDetails(collateralId)}
                                />
                            </div>
                        </div>
                    )
            ),
        ];

        return [
            ...massagedData.map(field => field ?? '-'),
        ];
    });

    return (
        <div className={Styles.container}>
            <Text
                label='Customer Collaterals'
                isBold
            />

            <Table
                isLoading={isLoading}
                header={tableHeader}
                body={tableBody}
            />

            <Pagination
                currentPage={index}
                maxPage={maxIndex}
                onPrev={handleOnPrev}
                onNext={handleOnNext}
                onFirst={handleOnFirst}
                onLast={handleOnLast}
            />
        </div>
    );
};

export default CustomerManagementCustomerCollateralTable;
